import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import PasswordlessLogin from './PasswordlessLogin';

function LoginModal({ isOpen, onClose }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [isLogin, setIsLogin] = useState(true);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [usePasswordless, setUsePasswordless] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  
  const { signIn, signUp, signInWithGoogle, signInWithApple, resetPassword, currentUser } = useAuth();

  useEffect(() => {
    // Close modal if user logs in
    if (currentUser && !currentUser.isAnonymous) {
      onClose();
    }
  }, [currentUser, onClose]);

  useEffect(() => {
    // Reset state when modal opens/closes
    if (isOpen) {
      setError('');
      setShowForgotPassword(false);
      setResetEmailSent(false);
      setUsePasswordless(false);
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }
    
    try {
      setError('');
      setLoading(true);
      
      if (isLogin) {
        await signIn(email, password);
      } else {
        await signUp(email, password, displayName);
      }
    } catch (error) {
      console.error("Authentication error:", error);
      // Provide friendly error messages for common auth errors
      if (error.code === 'auth/configuration-not-found') {
        setError('Authentication is not configured properly. Please try again later.');
      } else if (error.code === 'auth/email-already-in-use') {
        setError('This email is already registered. Try logging in instead.');
      } else if (error.code === 'auth/weak-password') {
        setError('Password is too weak. Use at least 6 characters.');
      } else if (error.code === 'auth/invalid-email') {
        setError('Please enter a valid email address.');
      } else if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
        setError('Invalid email or password.');
      } else {
        setError(error.message || 'An error occurred during authentication.');
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handleGoogleSignIn = async () => {
    try {
      setError('');
      setLoading(true);
      await signInWithGoogle();
    } catch (error) {
      console.error("Google Sign In Error:", error);
      if (error.code === 'auth/configuration-not-found') {
        setError('Google sign-in is not configured. Please use email login for now.');
      } else {
        setError(error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setError('Please enter your email address');
      return;
    }
    
    try {
      setError('');
      setLoading(true);
      await resetPassword(email);
      setResetEmailSent(true);
    } catch (error) {
      console.error("Password reset error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAppleSignIn = async () => {
    try {
      setError('');
      setLoading(true);
      await signInWithApple();
    } catch (error) {
      console.error("Apple Sign In Error:", error);
      if (error.code === 'auth/operation-not-allowed') {
        setError('Apple sign-in is not enabled. Please contact support.');
      } else {
        setError(error.message);
      }
    } finally {
      setLoading(false);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="modal-overlay">
      <div className="login-modal">
        <button className="close-button" onClick={onClose}>&times;</button>
        
        {usePasswordless ? (
          <PasswordlessLogin onClose={onClose} />
        ) : (
          <>
            <h2>{isLogin ? 'Log In' : 'Sign Up'}</h2>
            
            {error && <div className="auth-error">{error}</div>}
        
        <form onSubmit={handleSubmit}>
          {!isLogin && (
            <div className="form-group">
              <label htmlFor="displayName">Display Name</label>
              <input
                type="text"
                id="displayName"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Your display name"
              />
            </div>
          )}
          
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="your@email.com"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="Your password"
            />
          </div>
          
          <button 
            type="submit" 
            className="auth-button" 
            disabled={loading}
          >
            {loading 
              ? 'Loading...' 
              : isLogin 
                ? 'Log In' 
                : 'Sign Up'
            }
          </button>
        </form>
        
        {isLogin && !showForgotPassword && !resetEmailSent && (
          <div style={{ textAlign: 'center', marginTop: '10px' }}>
            <button 
              onClick={() => setShowForgotPassword(true)}
              className="text-button"
              style={{ 
                fontSize: '14px', 
                color: '#007bff', 
                background: 'none', 
                border: 'none', 
                cursor: 'pointer',
                textDecoration: 'underline'
              }}
            >
              Forgot Password?
            </button>
          </div>
        )}
        
        {showForgotPassword && !resetEmailSent && (
          <div style={{ marginTop: '15px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
            <h3 style={{ margin: '0 0 10px 0', fontSize: '16px' }}>Reset Password</h3>
            <p style={{ margin: '0 0 10px 0', fontSize: '14px' }}>
              Enter your email address and we'll send you a link to reset your password.
            </p>
            <button 
              onClick={handleForgotPassword}
              className="auth-button"
              disabled={loading || !email}
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </button>
            <button 
              onClick={() => setShowForgotPassword(false)}
              className="text-button"
              style={{ marginLeft: '10px' }}
            >
              Cancel
            </button>
          </div>
        )}
        
        {resetEmailSent && (
          <div style={{ marginTop: '15px', padding: '15px', backgroundColor: '#d4edda', borderRadius: '4px' }}>
            <p style={{ margin: 0, color: '#155724' }}>
              Password reset link sent to {email}. Check your email and follow the instructions.
            </p>
            <button 
              onClick={() => {
                setResetEmailSent(false);
                setShowForgotPassword(false);
              }}
              className="text-button"
              style={{ marginTop: '10px' }}
            >
              Back to Login
            </button>
          </div>
        )}
        
        <div className="auth-divider">
          <span>or</span>
        </div>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <button 
            onClick={handleGoogleSignIn} 
            className="google-button"
            disabled={loading}
            title="You need to enable Google Authentication in Firebase console first"
          >
            <img 
              src="https://developers.google.com/identity/images/g-logo.png" 
              alt="Google" 
              className="google-icon" 
            />
            Continue with Google
          </button>

          <button 
            onClick={handleAppleSignIn} 
            className="apple-button"
            disabled={loading}
            style={{
              backgroundColor: '#000',
              color: 'white',
              border: 'none',
              padding: '12px 20px',
              borderRadius: '4px',
              width: '100%',
              fontSize: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              cursor: 'pointer'
            }}
          >
            🍎 Continue with Apple
          </button>
        </div>
        
        <div style={{ textAlign: "center", marginTop: "10px", fontSize: "12px", color: "#888" }}>
          Note: Google login requires additional configuration in Firebase
        </div>
        
        <div className="auth-switch">
          {isLogin ? (
            <p>
              Don't have an account?{' '}
              <button 
                onClick={() => setIsLogin(false)}
                className="text-button"
              >
                Sign Up
              </button>
            </p>
          ) : (
            <p>
              Already have an account?{' '}
              <button 
                onClick={() => setIsLogin(true)}
                className="text-button"
              >
                Log In
              </button>
            </p>
          )}
          <p style={{ marginTop: '10px' }}>
            <button 
              onClick={() => setUsePasswordless(true)}
              className="text-button"
              style={{ fontSize: '14px', color: '#007bff' }}
            >
              ✨ Try Magic Link Login →
            </button>
          </p>
        </div>
        </>
        )}
        
      </div>
    </div>
  );
}

export default LoginModal;